import * as React from 'react';
import { Form, Formik } from 'formik';
import { SchoolEvent } from 'Src/models/event';
import { REPORT_PRINT_FORMAT, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/report';
import {
  ReportActions,
  ReportDescription,
  ReportFormatSelector,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from '../ReportFormUi';
import '../../SchoolEventView.scss';

interface Props {
  event: SchoolEvent;
  activeSchoolId: string;
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
}

interface ReportFormValues {
  isPrintFormName: boolean;
  isPrintYearGroup: boolean;
  isPrintGender: boolean;
  isPrintDOB: boolean;
  isPrintHouse: boolean;
  isPrintPP: boolean;
  isPrintSEN: boolean;
  isPrintFSM: boolean;
  isPrintPhotoConsentOptOut: boolean;
  isPrintPosition: boolean;
  isPrintMedicalInfo: boolean;
  isPrintNextOfKin: boolean;
  isPrintDescription: boolean;
  isPrintKitNotes: boolean;
  isPrintComments: boolean;
  isPrintTransportDetails: boolean;
  isPrintOppositionContactDetails: boolean;
  isPrintMembersOfStaff: boolean;
  isPrintEventTimings: boolean;
  printFormat: string;
}

interface ToggleOption {
  key: keyof Omit<ReportFormValues, 'printFormat'>;
  label: string;
  hint: string;
  isVisible?: boolean;
}

//report 0081
export const ExtendedParticipantListReportForm = ({ onCloseClick, onSubmit, event, activeSchoolId }: Props) => {
  const [schoolData, setSchoolData] = React.useState<any>(null);

  React.useEffect(() => {
    fetch('/dist/images/mis/misIntegrationSchoolsList.json')
      .then(response => response.json())
      .then(data => {
        const matchedSchoolData = data.find(item => item.schoolSITId === activeSchoolId);
        setSchoolData(matchedSchoolData);
      })
      .catch(error => console.error('Error loading JSON:', error));
  }, [activeSchoolId]);

  const { id: eventId } = event;

  const requestInitial: ReportFormValues = {
    isPrintFormName: true,
    isPrintYearGroup: false,
    isPrintGender: false,
    isPrintDOB: false,
    isPrintHouse: false,
    isPrintPP: schoolData ? schoolData.studentsPPInfo === 1 : false,
    isPrintSEN: schoolData ? schoolData.studentsSENInfo === 1 : false,
    isPrintFSM: schoolData ? schoolData.studentsFSMInfo === 1 : false,
    isPrintPhotoConsentOptOut: false,
    isPrintPosition: true,
    isPrintMedicalInfo: schoolData ? schoolData.studentsMedicalInfo === 1 : false,
    isPrintNextOfKin: true,
    isPrintDescription: true,
    isPrintKitNotes: true,
    isPrintComments: true,
    isPrintTransportDetails: true,
    isPrintOppositionContactDetails: false,
    isPrintMembersOfStaff: true,
    isPrintEventTimings: true,
    printFormat: REPORT_PRINT_FORMAT.PDF
  };

  const studentDetailsOptions: ToggleOption[] = [
    { key: 'isPrintFormName', label: 'Show school form', hint: 'Include each student form group.' },
    { key: 'isPrintYearGroup', label: 'Show year group', hint: 'Include each student year group.' },
    { key: 'isPrintGender', label: 'Show gender', hint: 'Include each student assigned gender.' },
    { key: 'isPrintDOB', label: 'Show date of birth', hint: 'Include each student date of birth.' },
    { key: 'isPrintHouse', label: 'Show house', hint: 'Include each student house.' },
    {
      key: 'isPrintPP',
      label: 'Show PP',
      hint: 'Include PP information.',
      isVisible: !!schoolData && schoolData.studentsPPInfo === 1
    },
    {
      key: 'isPrintSEN',
      label: 'Show SEN',
      hint: 'Include SEN information.',
      isVisible: !!schoolData && schoolData.studentsSENInfo === 1
    },
    {
      key: 'isPrintFSM',
      label: 'Show FSM',
      hint: 'Include FSM information.',
      isVisible: !!schoolData && schoolData.studentsFSMInfo === 1
    },
    { key: 'isPrintPosition', label: 'Show positions', hint: 'Include assigned positions on the pitch.' },
    {
      key: 'isPrintMedicalInfo',
      label: 'Show medical info',
      hint: 'Include medical information relating to students.',
      isVisible: !!schoolData && schoolData.studentsMedicalInfo === 1
    },
    {
      key: 'isPrintNextOfKin',
      label: 'Show next of kin details',
      hint: 'Include emergency contact details for students.'
    },
    {
      key: 'isPrintPhotoConsentOptOut',
      label: 'Show photo consent opt-out',
      hint: 'Mark students whose photo consent has been withdrawn.'
    }
  ];

  const eventDetailsOptions: ToggleOption[] = [
    { key: 'isPrintDescription', label: 'Show event description', hint: 'Include the event description.' },
    { key: 'isPrintKitNotes', label: 'Show kit notes', hint: 'Include kit information added to the event.' },
    { key: 'isPrintComments', label: 'Show event comments', hint: 'Include internal event comments.' },
    {
      key: 'isPrintTransportDetails',
      label: 'Show transport details',
      hint: 'Include travel and transport information.'
    },
    {
      key: 'isPrintOppositionContactDetails',
      label: 'Show opposition contact details',
      hint: 'Include available opposition contacts.'
    },
    {
      key: 'isPrintMembersOfStaff',
      label: 'Show coach and staff names',
      hint: 'Include staff assigned to the event.'
    },
    { key: 'isPrintEventTimings', label: 'Show event timings', hint: 'Include meet, departure and event times.' }
  ];

  const renderToggle = (
    option: ToggleOption,
    values: ReportFormValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (option.isVisible === false) {
      return null;
    }

    return (
      <ReportToggle
        key={option.key}
        name={option.key}
        label={option.label}
        hint={option.hint}
        value={values[option.key]}
        onChange={value => setFieldValue(option.key, value)}
      />
    );
  };

  return (
    <Formik
      onSubmit={values => {
        const { printFormat, ...options } = values;
        const data = {
          schoolId: activeSchoolId,
          eventId,
          ...options,
          isPrintXLSX: printFormat === REPORT_PRINT_FORMAT.XLSX,
          isPrintPDF: printFormat === REPORT_PRINT_FORMAT.PDF
        };

        onSubmit(data, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.EXTENDED_PARTICIPANT_LIST);
      }}
      initialValues={requestInitial}
      enableReinitialize
    >
      {({ values, setFieldValue }) => (
        <Form className="eReportForm">
          <ReportDescription>
            Create a detailed participant list with student, safeguarding and event information for the selected event.
          </ReportDescription>

          <ReportSection
            number={1}
            title="Student details"
            hint="Choose which participant details should appear in the generated document."
          >
            <ReportToggleList>
              {studentDetailsOptions.map(option => renderToggle(option, values, setFieldValue))}
            </ReportToggleList>
          </ReportSection>

          <ReportSection
            number={2}
            title="Event details"
            hint="Choose which event details should be included with the participant list."
          >
            <ReportToggleList>
              {eventDetailsOptions.map(option => renderToggle(option, values, setFieldValue))}
            </ReportToggleList>
          </ReportSection>

          <ReportSection number={3} title="Output format" hint="Choose a printable PDF or an editable Excel file.">
            <ReportFormatSelector
              value={values.printFormat}
              pdfValue={REPORT_PRINT_FORMAT.PDF}
              xlsxValue={REPORT_PRINT_FORMAT.XLSX}
              onChange={value => setFieldValue('printFormat', value)}
            />
          </ReportSection>

          <ReportActions onCancel={onCloseClick} />
        </Form>
      )}
    </Formik>
  );
};
